import TokenRepository from "@/repositories/tokenRepository";
import UserRepository from "@/repositories/userRepository";
import type { UserCreatePayload, UserLoginPayload } from "@/types";
import { jwtDecode } from "jwt-decode";
import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { useCellsStore } from "./cells";
import { useCropsStore } from "./crops";
import { useFarmStore } from "./farms";
import { useGardenStore } from "./garden";
import { useHarvestsStore } from "./harvests";
import { useSeedsStore } from "./seeds";
import { useTaxaStore } from "./taxa";
import { useUserStore } from "./user";

const EXP_MARGIN_SEC = 30;

interface DecodedToken {
  exp: number;
}

// Consider a token expired 30 seconds before it actually is.
function isExpired(token: string): boolean {
  const { exp } = jwtDecode<DecodedToken>(token);
  return Date.now() >= (exp - EXP_MARGIN_SEC) * 1000;
}

function expiresIn(token: string): number {
  const { exp } = jwtDecode<DecodedToken>(token);
  return (exp - EXP_MARGIN_SEC) * 1000 - Date.now();
}

export const useAuthStore = defineStore("auth", () => {
  const accessToken = ref<string | null>(localStorage.getItem("accessToken"));
  const refreshToken = ref<string | null>(localStorage.getItem("refreshToken"));

  // Handle so we can clearTimeout on logout.
  let refreshTimer: ReturnType<typeof setTimeout> | null = null;

  const isAuthenticated = computed(() => accessToken.value !== null && !isExpired(accessToken.value));

  function setAccessToken(access: string) {
    accessToken.value = access;
    localStorage.setItem("accessToken", access);
  }

  function setRefreshToken(refresh: string) {
    refreshToken.value = refresh;
    localStorage.setItem("refreshToken", refresh);
  }

  function clearAuthData() {
    accessToken.value = null;
    refreshToken.value = null;
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
  }

  // Fetch everything the app needs after a successful (auto-)login. Failures are
  // isolated (one failing fetch doesn't abort the others) and logged.
  async function afterLogin() {
    const results = await Promise.allSettled([
      useUserStore().getUser(),
      useFarmStore().getFarm(),
      useTaxaStore().getTaxa(),
      useCropsStore().getCrops(),
      useSeedsStore().getSeeds(),
      useGardenStore().getGardens(),
    ]);
    for (const result of results) {
      if (result.status === "rejected") console.error("afterLogin fetch failed:", result.reason);
    }
  }

  function autoRefreshToken() {
    if (refreshTimer) clearTimeout(refreshTimer);
    if (!accessToken.value) return;

    refreshTimer = setTimeout(() => {
      refreshTimer = null;
      const refresh = refreshToken.value;
      // If the user logged out during the timeout, the token is gone.
      if (!refresh) return;

      if (isExpired(refresh)) {
        logout();
      } else {
        TokenRepository.refresh(refresh)
          .then(({ data: { access } }: { data: { access: string } }) => {
            setAccessToken(access);
            autoRefreshToken();
          })
          .catch((err) => {
            console.error(err);
            logout();
          });
      }
    }, expiresIn(accessToken.value));
  }

  async function signup(user: UserCreatePayload) {
    try {
      await UserRepository.create(user);
    } catch (err) {
      throw (err as { response?: unknown }).response;
    }
    // TODO: it would be nice if signup returned a token directly.
    await login(user);
  }

  async function login(user: UserLoginPayload) {
    const { data } = await TokenRepository.get(user);
    setAccessToken(data.access);
    setRefreshToken(data.refresh);
    autoRefreshToken();
    afterLogin();
  }

  async function tryAutoLogin() {
    const access = accessToken.value;
    const refresh = refreshToken.value;
    // Both or none should be present anyway.
    if (!access || !refresh) return;
    if (isExpired(refresh)) {
      logout();
      return;
    }

    // If the access token is already expired, refresh it BEFORE firing the data
    // fetches in afterLogin — otherwise those requests 401 and the response
    // interceptor logs the user out before any refresh can run.
    if (isExpired(access)) {
      try {
        const { data } = await TokenRepository.refresh(refresh);
        setAccessToken(data.access);
      } catch (err) {
        console.error("Auto-refresh on boot failed:", err);
        logout();
        return;
      }
    }

    autoRefreshToken();
    afterLogin();
  }

  function logout(): Promise<void> {
    if (refreshTimer) {
      clearTimeout(refreshTimer);
      refreshTimer = null;
    }
    clearAuthData();
    useGardenStore().resetGardens();
    useCellsStore().resetCells();
    useHarvestsStore().resetHarvests();
    return Promise.resolve();
  }

  return {
    accessToken,
    refreshToken,
    isAuthenticated,
    setAccessToken,
    setRefreshToken,
    signup,
    login,
    tryAutoLogin,
    autoRefreshToken,
    afterLogin,
    logout,
  };
});
